import * as Bluebird from 'bluebird';
import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { trigger, transition, style, animate, state } from '@angular/animations';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { AppService } from 'app/app.service';
import { AuthService } from 'app/services/auth/auth.service';
import { Router } from '@angular/router';
import { BaseComponent } from 'app/shared/components';
import { TranslateService } from '@ngx-translate/core';
import { ClientService } from 'app/services/client/client.service';
import { IKuiSelectOption } from 'app/key-ui';
import { AuthInfo, AuthEndpoint } from '@key-telematics/fleet-api-client';
import { OidcAuthService } from 'app/services/oidc/oidc.service';
import { KeyTranslateLoader } from 'app/services/translate/translate-loader';

export interface UserInfo {
    id: string;
    name: string;
    companyName: string;
    companyType: string;
    avatar?: string;
}

@Component({
    selector: 'key-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    animations: [
        trigger(
            'fadeAnimation',
            [
                state(':enter', style({ 'overflow-y': 'hidden' })),
                state(':leave', style({ 'overflow-y': 'hidden' })),
                transition(
                    ':enter', [
                        style({ opacity: 0, 'height': 0 }),
                        animate('250ms', style({ 'opacity': 1, height: '*' })),
                    ]
                ),
                transition(
                    ':leave', [
                        style({ 'opacity': 1, height: '*' }),
                        animate('250ms', style({ 'opacity': 0, height: 0 })),

                    ]
                )]
        ),
    ],
})
export class LoginComponent extends BaseComponent implements OnInit {

    loginForm: FormGroup;
    users: UserInfo[] = null;

    logoPath: string = null;
    loading = false;

    activeButton: any;

    ready = false;
    errorMessage: string;
    reloading = false;

    inputsEnabled = true;
    authInfo: AuthInfo;

    loginMessage: string;

    apiEndpoints: IKuiSelectOption[];
    languages: IKuiSelectOption[];

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private app: AppService,
        private clientService: ClientService,
        private auth: AuthService,
        @Inject(DOCUMENT) private document: HTMLDocument,
        fb: FormBuilder,
        private i18n: TranslateService,
        private oidcAuthService: OidcAuthService
    ) {
        super();

        // if we've been navigating around the app before, we need to do a hard reload
        // to clear the browser of any of our previous state.
        if (this.router.navigated) {
            this.reloadWindow();
        }

        this.apiEndpoints = this.app.env.apiEndpoints ? this.app.env.apiEndpoints
            .map((x, i) => ({
                id: i,
                value: x,
                // tslint:disable-next-line: tsr-detect-possible-timing-attacks
                selected: this.app.env.apiEndpoint ? x === this.app.env.apiEndpoint : i === 0,
            })) : [];


        const languageEndpoint = KeyTranslateLoader.getLanguageEndpoint();
        this.languages = [
            { id: 'auto', name: 'Automatic' },
            ...KeyTranslateLoader.getLanguages().filter(x => x.id !== 'en'), // remove english, it's not retrievable from the poeditor endpoint
        ].map((item, i) => ({
            id: item.id,
            value: item.name,
            selected: languageEndpoint ? languageEndpoint.lang === item.id : i === 0,
        }));

        this.loginForm = fb.group({
            username: ['', [Validators.required]],
            password: ['', [Validators.required]],
        });

        this.route.params.subscribe(param => {

            switch (param['reason']) {
                case 'expired':
                    this.errorMessage = this.i18n.instant('LOGIN.ERROR_EXPIRED');
                    break;
                default:
                    this.errorMessage = null;
                    break;
            }
        });

        this.route.queryParams.pipe(takeUntil(this.destroyed)).subscribe(params => {
            if (params.token) {
                // if we're sure about the endpoint, simply do the login immediately
                if (!this.apiEndpoints || this.apiEndpoints.length === 1) {
                    this.doLogin('token', params.token);
                } else {
                    // on staging, we need to select an endpoint, so stuff the token in the password field and 
                    // use the word "Token" for the user name, then disable the inputs
                    this.loginForm.setValue({
                        username: 'Token',
                        password: params.token,
                    });
                    this.inputsEnabled = false;
                }
            } else {
                if (!this.inputsEnabled) { // allow removing the token from the url manually
                    this.inputsEnabled = true;
                    this.loginForm.setValue({
                        username: null,
                        password: null,
                    });

                }
            }
        });

    }

    async ngOnInit() {

        this.on(this.app.theme$, theme => {
            this.logoPath = theme.logoPath;
            this.authInfo = this.app.domainInfo && this.app.domainInfo.auth;
            this.loginMessage = this.app.domainInfo && this.app.domainInfo.message;
        });

        if (this.app.entryUrl) {
            const params = this.oidcAuthService.decodeUrlParameters<{ id_token: string }>(this.app.entryUrl);
            if (params.id_token) {
                this.app.entryUrl = null; // clear this so we don't try log in again when the user logs out
                await this.doLogin('oidc', params.id_token);
            }
        }        
        
        this.ready = true;
    }

    reloadWindow() {
        this.reloading = true;
        window.location.reload();
    }

    showError(error: any) {
        this.users = null;

        if (error) {
            switch (error.name) {
                case 'TooManyRequestsError':
                    this.errorMessage = this.i18n.instant('LOGIN.ERROR_TOO_MANY_ATTEMPTS');
                    break;

                case 'UnauthorizedError':
                    this.errorMessage = this.i18n.instant('LOGIN.ERROR_CREDENTIALS');
                    break;

                default:
                    this.errorMessage = error.message || error;
            }
        } else {
            this.errorMessage = null;
        }
    }

    apiEndpointSelected(items: { value: string }[]) {
        if (items && items.length > 0) {
            this.app.setApiEndpoint(items[0].value);
        }
    }
    
    languageSelected(items: { id: string, value: string }[]) {
        if (items && items.length > 0) {
            const lang = items[0].id;
            if (lang === 'auto') {
                KeyTranslateLoader.clearLanguageEndpoint();
            } else {
                KeyTranslateLoader.setLanguageEndpoint(lang, `${this.app.env.apiEndpoint}/translate/${lang}/export`);
            }
            window.location.reload();
        }
    }

    async doLogin(type: 'password' | 'token' | 'oidc', token?: string): Promise<void> {

        (this.document.activeElement as any).blur(); // mobile bug fix - when submitting the form through the keyboards action, the keyboard stays open.

        this.showError(null);
        this.loading = true;
        try {
            const username = this.loginForm.value.username.toLowerCase();
            const password = this.loginForm.value.password;
            if (!this.inputsEnabled && username === 'token') {
                token = password; // do token based authentication
                type = 'token';
            }

            const signInAndGetUsers = (): Bluebird<UserInfo[]> => {
                switch (type) {
                    case 'token': return this.auth.signInWithToken(token);
                    case 'oidc': return this.auth.signInWithOpenIdConnect(this.app.domain, token);
                    default:
                        return this.auth.signInAndGetMatchingUsers(username, password);
                }
            };

            const users = await signInAndGetUsers();

            if (users.length === 0) { throw new Error('No users matching your credentials were found'); } // should never happen, but just in case
            if (users.length === 1) {
                return this.doUserSelected(users[0].id, type === 'token'); // we have only one user, don't bother showing a user list
            } else {
                this.users = users
                    .sort((a, b) => {
                        const order = {
                            system: 4,
                            distributor: 3,
                            vendor: 2,
                            client: 1,
                        };

                        const aOrder = order[a.companyType.toLowerCase()];
                        const bOrder = order[b.companyType.toLowerCase()];

                        return aOrder > bOrder ? -1 : 1;
                    }); // we can't tell which user to select, let the user decide
            }
        } catch (err) {
            this.showError(err);
        } finally {
            this.loading = false;
            this.activeButton = null;
        }

    }

    doUserSelected(id: string, token?: boolean): Bluebird<void> {
        if (id) {
            const next = this.route.snapshot.params.next;

            return this.auth.selectAuthenticatedUser(id)
                .then(user => {
                    if (token) { // if a login token was used to log in, go straight to the welcome screen
                        this.router.navigate(['welcome']);
                    } else {
                        if (user.defaultClientId) {
                            return this.clientService.loadClient(user.defaultClientId).then(() => {
                                return this.router.navigate(next ? [next] : ['status']).then(result => {
                                    if (!result) { // if this fails (which can happen if a user is attempting to return to a page they no longer have access to, just try go to status again
                                        this.router.navigate(['status']);
                                    }
                                });
                            }).catch(err => {
                                this.app.logError(err);
                                this.router.navigate(['client-select', { next }]);
                            });
                        } else {
                            this.router.navigate(['client-select', { next }]);
                        }
                    }
                })
                .catch(err => this.showError(err));
        } else {
            this.users = null;
            return Bluebird.resolve();
        }
    }


    async loginWithOpenId(endpoint: AuthEndpoint) {
        this.loading = true;
        try {
            const oauthService = await this.oidcAuthService.getOAuthService(endpoint.endpoint, endpoint.clientId, window.location.origin);
            oauthService.initImplicitFlow();
        } catch (err) {
            this.showError(err);
        } finally {
            this.loading = false;
        }
    }


}
